import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { ordersAPI } from '../services/api';
import { 
  Clipboard, 
  CheckCircle, 
  Truck, 
  Package, 
  XCircle,
  CreditCard,
  MapPin,
  Calendar,
  Share2,
  Loader2,
  ArrowLeft,
  Check,
  Clock,
  TriangleAlert,
  Home,
  Phone,
  Mail,
  RefreshCcw,
  ShoppingCart,
  Printer
} from 'lucide-react';
import Skeleton from './SkeletonOrderItems';
// Uses global header via Layout

const OrderStatus = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loadCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [statusChanged, setStatusChanged] = useState(false);
  const [reordering, setReordering] = useState(false);
  const previousOrderStatusRef = useRef(null);

  const fetchOrder = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const response = await ordersAPI.getById(id);
      const newOrder = response.data;
      
      // Check if status changed using ref
      if (previousOrderStatusRef.current && previousOrderStatusRef.current !== newOrder.status) {
        setStatusChanged(true);
        setTimeout(() => setStatusChanged(false), 5000); // Hide after 5 seconds
      }
      
      previousOrderStatusRef.current = newOrder.status;
      setOrder(newOrder);
      setLastUpdated(new Date());
    } catch (err) {
      setError('Failed to fetch order details');
      console.error('Error fetching order:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Auto-refresh every 30 seconds for pending/confirmed/shipped orders
  useEffect(() => {
    const orderStatus = order?.status;
    if (!orderStatus || orderStatus === 'delivered' || orderStatus === 'cancelled') {
      return;
    }

    const interval = setInterval(() => {
      fetchOrder(true);
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [order?.status, fetchOrder]);

  const handleRefresh = () => {
    fetchOrder(true);
  };

  const handleReorder = async () => {
    if (!order) return;
    
    setReordering(true);
    try {
      const response = await ordersAPI.reorder(order.id);
      
      if (response.status === 200) {
        // Reload cart items after reorder
        await loadCart();
        
        // Show detailed success message
        let message = response.data.message;
        
        // Add details about unavailable items
        if (response.data.unavailable_items && response.data.unavailable_items.length > 0) {
          message += '\n\nUnavailable items:\n';
          response.data.unavailable_items.forEach(item => {
            message += `• ${item.name} (${item.reason || 'Out of stock'})\n`;
          });
        }
        
        // Add details about low stock items
        if (response.data.low_stock_items && response.data.low_stock_items.length > 0) {
          message += '\n\nItems with reduced quantities:\n';
          response.data.low_stock_items.forEach(item => {
            message += `• ${item.name}: ${item.added}/${item.requested} added\n`;
          });
        }
        
        alert(message);
        
        // Navigate to cart/checkout
        navigate('/checkout');
      } else {
        throw new Error('Failed to reorder');
      }
    } catch (err) {
      console.error('Error reordering:', err);
      
      // Handle different types of errors
      if (err.response?.status === 404) {
        alert('Order not found. This order may have been deleted or you may not have permission to access it.');
      } else if (err.response?.data?.error) {
        alert(`Reorder failed: ${err.response.data.error}`);
      } else {
        alert('Failed to reorder items. Please try again.');
      }
    } finally {
      setReordering(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: {
        title: 'Order Placed',
        description: 'Your order has been placed and is being processed',
        icon: Clipboard,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        borderColor: 'border-yellow-300'
      },
      confirmed: {
        title: 'Order Confirmed',
        description: 'Your order has been confirmed and is being prepared',
        icon: CheckCircle,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-300'
      },
      shipped: {
        title: 'Order Shipped',
        description: 'Your order has been shipped and is on its way',
        icon: Truck,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        borderColor: 'border-purple-300'
      },
      delivered: {
        title: 'Order Delivered',
        description: 'Your order has been successfully delivered',
        icon: Package,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-300'
      },
      cancelled: {
        title: 'Order Cancelled',
        description: 'Your order has been cancelled',
        icon: XCircle,
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-300'
      }
    };
    return statusMap[status] || statusMap.pending;
  };

  const getStatusSteps = () => {
    const steps = [
      { key: 'pending', title: 'Order Placed', icon: Clipboard, description: 'Order placed and being processed' },
      { key: 'confirmed', title: 'Confirmed', icon: CheckCircle, description: 'Order confirmed and being prepared' },
      { key: 'shipped', title: 'Shipped', icon: Truck, description: 'Order shipped and on its way' },
      { key: 'delivered', title: 'Delivered', icon: Package, description: 'Order successfully delivered' }
    ];

    const currentStatusIndex = steps.findIndex(step => step.key === order?.status);
    
    return steps.map((step, index) => {
      const isCompleted = index <= currentStatusIndex;
      const isCurrent = index === currentStatusIndex;
      const isCancelled = order?.status === 'cancelled';
      
      return {
        ...step,
        isCompleted: isCompleted && !isCancelled,
        isCurrent: isCurrent && !isCancelled,
        isCancelled: isCancelled && step.key === 'cancelled'
      };
    });
  };

  const getPaymentStatusInfo = (paymentStatus) => {
    switch (paymentStatus?.toLowerCase()) {
      case 'paid':
        return { color: 'text-green-600', bgColor: 'bg-green-100', icon: Check, label: 'Paid' };
      case 'pending':
        return { color: 'text-yellow-600', bgColor: 'bg-yellow-100', icon: Clock, label: 'Pending' };
      case 'failed':
        return { color: 'text-red-600', bgColor: 'bg-red-100', icon: TriangleAlert, label: 'Failed' };
      default:
        return { color: 'text-gray-600', bgColor: 'bg-gray-100', icon: Clock, label: paymentStatus || 'Unknown' };
    }
  };

  const getEstimatedDelivery = () => {
    if (!order) return null;
    
    const orderDate = new Date(order.created_at);
    const estimatedDays = {
      pending: 5,
      confirmed: 4,
      shipped: 2,
      delivered: 0
    };
    
    const days = estimatedDays[order.status] || 5;
    const deliveryDate = new Date(orderDate);
    deliveryDate.setDate(deliveryDate.getDate() + days);
    
    return deliveryDate;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Skeleton type="order" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Order Not Found</h1>
          <p className="text-gray-600 mb-4">{error || 'The order you are looking for does not exist.'}</p>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const handleShare = async () => {
    const url = `${window.location.origin}/share/orders/${order.id}`;
    const title = `Track Order #${order.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
      } else {
        await navigator.clipboard.writeText(url);
        alert('Order tracking link copied to clipboard');
      }
    } catch {}
  };

  const statusInfo = getStatusInfo(order.status);
  const statusSteps = getStatusSteps();
  const estimatedDelivery = getEstimatedDelivery();
  const paymentStatusInfo = getPaymentStatusInfo(order.payment_status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Status Update Notification */}
      {statusChanged && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4 shadow-sm">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="text-green-500 text-xl" strokeWidth={1.5} />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">
                Order Status Updated! Your order status has been updated.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {/* Back Button */}
          <Link
            to="/orders"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm font-medium">Back to Orders</span>
          </Link>

          {/* Order Header Card */}
          <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-6 mb-6 border border-gray-200">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Order #{order.id}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs sm:text-sm font-semibold border ${statusInfo.bgColor} ${statusInfo.color} ${statusInfo.borderColor}`}>
                    <statusInfo.icon className="w-3 h-3" strokeWidth={1.5} />
                    {statusInfo.title}
                  </span>
                  <div className="flex items-center gap-4">
                    {(order.payment_status === 'success' || order.payment_status === 'paid' || ['confirmed', 'shipped', 'delivered'].includes(order.status)) && (
                      <button 
                        onClick={() => window.open(`/orders/${order.id}/invoice`, '_blank')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
                        title="Print Invoice"
                      >
                        <Printer className="w-5 h-5 text-gray-600 group-hover:text-emerald-600" strokeWidth={1.5} />
                      </button>
                    )}
                    <button 
                      onClick={handleShare}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors group"
                      title="Share Order"
                    >
                      <Share2 className="w-4 h-4 text-gray-600 group-hover:text-blue-600" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                    <span>Placed on {new Date(order.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>
                  {lastUpdated && (
                    <div className="flex items-center gap-2">
                      <RefreshCcw className={`w-4 h-4 text-gray-400 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                      <span className="text-xs">Last updated: {lastUpdated.toLocaleTimeString()}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {estimatedDelivery && order.status !== 'delivered' && order.status !== 'cancelled' && (
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Calendar className="text-blue-600 text-xl" strokeWidth={1.5} />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-900">Estimated Delivery</h3>
                    <p className="text-sm text-blue-700">
                      {estimatedDelivery.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} 
                      {' '}({Math.ceil((estimatedDelivery - new Date()) / (1000 * 60 * 60 * 24))} days)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Left Column - Order Status Timeline */}
            <div className="lg:col-span-2 space-y-6">
              {/* Order Status Timeline */}
              <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">Order Progress</h2>
                  {order.status !== 'delivered' && order.status !== 'cancelled' && (
                    <button
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                      title="Refresh order status"
                    >
                      <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                      Refresh
                    </button>
                  )}
                </div>
                
                <div className="flow-root">
                  <ul className="-mb-8">
                    {statusSteps.map((step, stepIdx) => (
                      <li key={step.key}>
                        <div className="relative pb-8">
                          {stepIdx !== statusSteps.length - 1 ? (
                            <span
                              className={`absolute top-5 left-5 -ml-px h-full w-0.5 ${
                                step.isCompleted ? 'bg-green-500' : 'bg-gray-200'
                              }`}
                              aria-hidden="true"
                            />
                          ) : null}
                          <div className="relative flex space-x-4">
                            <div>
                              <span
                                className={`h-10 w-10 rounded-full flex items-center justify-center ring-4 ring-white shadow-sm ${
                                  step.isCompleted
                                    ? 'bg-green-500 text-white'
                                    : step.isCurrent
                                    ? 'bg-blue-500 text-white animate-pulse'
                                    : step.isCancelled
                                    ? 'bg-red-500 text-white'
                                    : 'bg-gray-300 text-gray-500'
                                }`}
                              >
                                <step.icon className="w-5 h-5" strokeWidth={1.5} />
                              </span>
                            </div>
                            <div className="min-w-0 flex-1 pt-1.5">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p
                                    className={`text-base font-semibold mb-1 ${
                                      step.isCompleted || step.isCurrent || step.isCancelled
                                        ? 'text-gray-900'
                                        : 'text-gray-500'
                                    }`}
                                  >
                                    {step.title}
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    {step.description}
                                  </p>
                                </div>
                                {step.isCompleted && step.key === 'pending' && (
                                  <div className="text-right text-xs text-gray-500 whitespace-nowrap ml-4">
                                    {new Date(order.created_at).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Order Items */}
              <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-4 sm:p-6 border border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
                  <h2 className="text-lg sm:text-xl font-bold text-gray-900">Order Items</h2>
                  <span className="text-xs sm:text-sm text-gray-500 font-medium">
                    {order.order_items.length} {order.order_items.length === 1 ? 'item' : 'items'}
                  </span>
                </div>
                <div className="space-y-2">
                  {order ? order.order_items.map((item, index) => (
                    <div 
                      key={item.id} 
                      className="group relative flex items-center gap-3 sm:gap-4 p-3 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 hover:border-green-300 transition-all duration-200"
                    >
                      {/* Product Image */}
                      <div className="relative flex-shrink-0">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border border-gray-200 shadow-sm">
                          <img
                            src={item.product.image_url}
                            alt={item.product.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/80x80?text=No+Image';
                            }}
                          />
                        </div>
                      </div>

                      {/* Product Details */}
                      <div className="flex-1 min-w-0 flex items-center justify-between gap-3 sm:gap-4">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm sm:text-base font-semibold text-gray-900 mb-0.5 group-hover:text-green-700 transition-colors duration-200 truncate">
                            {item.product.name}
                          </h3>
                          <div className="flex items-center gap-2 flex-wrap">
                            {item.product.category && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                                {item.product.category}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">
                              Qty: <span className="font-semibold text-gray-700">{item.quantity}</span>
                            </span>
                            <span className="text-xs text-gray-500">
                              @ {item.product.formatted_price}
                            </span>
                          </div>
                        </div>
                        
                        {/* Total Price */}
                        <div className="text-right flex-shrink-0">
                          <p className="text-base sm:text-lg font-bold text-gray-900">
                            {item.formatted_total_price}
                          </p>
                        </div>
                      </div>
                    </div>
                  )) : Array.from({length: 3}).map((_,i) => <Skeleton type="item" key={i} />)}
                </div>
              </div>
            </div>

            {/* Right Column - Payment & Shipping Info */}
            <div className="space-y-6">
              {/* Payment Information */}
              <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CreditCard className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Payment</h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Payment Status</p>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${paymentStatusInfo.bgColor} ${paymentStatusInfo.color}`}>
                      <paymentStatusInfo.icon className="w-4 h-4" strokeWidth={1.5} />
                      {paymentStatusInfo.label}
                    </div>
                  </div>
                  {order.payment_provider && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Payment Method</p>
                      <p className="text-sm font-medium text-gray-900 uppercase">{order.payment_provider}</p>
                    </div>
                  )}
                  {order.payment_reference && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Payment Reference</p>
                      <p className="text-sm font-mono text-gray-900 break-all">{order.payment_reference}</p>
                    </div>
                  )}
                  <div className="pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Total Amount</p>
                    <p className="text-xl font-bold text-gray-900">{order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}</p>
                  </div>
                </div>
              </div>

              {/* Shipping Details */}
              <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Truck className="w-5 h-5 text-green-600" strokeWidth={1.5} />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Shipping Details</h2>
                </div>
                <div className="space-y-4">
                  {/* Shipping Status */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Shipping Status</p>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${
                      order.status === 'shipped' || order.status === 'delivered' 
                        ? 'bg-green-100 text-green-600' 
                        : order.status === 'cancelled'
                        ? 'bg-red-100 text-red-600'
                        : 'bg-yellow-100 text-yellow-600'
                    }`}>
                      {(() => {
                        const Icon = order.status === 'shipped' || order.status === 'delivered' ? Truck : Clock;
                        return <Icon className="w-4 h-4" strokeWidth={1.5} />;
                      })()}
                      {order.status === 'shipped' ? 'Shipped' : 
                       order.status === 'delivered' ? 'Delivered' : 
                       order.status === 'cancelled' ? 'Cancelled' : 
                       'Preparing for Shipment'}
                    </div>
                  </div>

                  {/* Shipping Address */}
                  {order.user?.address && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex items-start gap-3 mb-3">
                        <MapPin className="w-4 h-4 text-green-600 mt-1 flex-shrink-0" strokeWidth={1.5} />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 mb-1">Delivery Address</p>
                          <p className="text-sm text-gray-700 leading-relaxed">{order.user.address}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  {(order.user?.name || order.user?.phone || order.user?.email) && (
                    <div className="pt-3 border-t border-gray-200 space-y-2">
                      <p className="text-xs text-gray-500 mb-2">Contact Information</p>
                      {order.user?.name && (
                        <div className="flex items-center gap-3">
                          <Home className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-xs text-gray-500">Recipient Name</p>
                            <p className="text-sm font-medium text-gray-900">{order.user.name}</p>
                          </div>
                        </div>
                      )}
                      {order.user?.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-xs text-gray-500">Phone Number</p>
                            <p className="text-sm font-medium text-gray-900">{order.user.phone}</p>
                          </div>
                        </div>
                      )}
                      {order.user?.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-gray-400" strokeWidth={1.5} />
                          <div>
                            <p className="text-xs text-gray-500">Email Address</p>
                            <p className="text-sm font-medium text-gray-900">{order.user.email}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Estimated Delivery */}
                  {estimatedDelivery && order.status !== 'delivered' && order.status !== 'cancelled' && (
                    <div className="pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-blue-600" strokeWidth={1.5} />
                        <div>
                          <p className="text-xs text-gray-500">Estimated Delivery</p>
                          <p className="text-sm font-medium text-gray-900">
                            {estimatedDelivery.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 p-6 border border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Subtotal</span>
                    <span>₹{(Number(order.items_total) || (Number(order.total_amount || 0) - Number(order.delivery_charge !== undefined ? order.delivery_charge : 50))).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mb-2">
                    <span>Delivery Charge</span>
                    <span className="text-green-600">+ ₹{Number(order.delivery_charge !== undefined ? order.delivery_charge : 50).toFixed(2)}</span>
                  </div>
                  <hr className="border-gray-200" />
                  <div className="flex justify-between text-lg font-bold pt-2">
                    <span className="text-gray-900">Total</span>
                    <span className="text-green-600">{order.formatted_total_amount || `₹${parseFloat(order.total_amount || 0).toFixed(2)}`}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            {order.status !== 'cancelled' && (
              <button 
                onClick={handleReorder}
                disabled={reordering}
                className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-sm hover:shadow-md disabled:hover:shadow-sm"
              >
                {reordering ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    Adding to Cart...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4" strokeWidth={1.5} />
                    Reorder
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderStatus;
